/**
 * WHAT:
 *   Formatting utility functions for data display.
 *
 * WHY:
 *   Centralized formatting logic for consistent data presentation.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Utils → Formatters
 *
 * DEPENDENCIES:
 *   None - Pure utility functions
 *
 * RISKS:
 *   - NOTE: Number parsing may fail for edge cases (scientific notation, etc.)
 *   - NOTE: Comma-to-dot conversion assumes European number format
 */

/**
 * WHAT:
 *   Formats a number to 2 decimal places, handling comma-to-dot conversion.
 *
 * WHY:
 *   Normalizes European number format (comma as decimal separator) to JavaScript format.
 *
 * INPUTS:
 *   - v: unknown — Value to format (string or number)
 *
 * OUTPUT:
 *   string — Formatted number string with 2 decimal places, or original string if not numeric.
 *
 * @param v - Value to format
 *
 * @returns {string} Formatted number string with 2 decimal places, or original string if not numeric
 *
 */
export const formatNumber2 = (v: unknown): string => {
  if (v === null || v === undefined) {
    return "";
  }
  if (
    typeof v !== "string" &&
    typeof v !== "number" &&
    typeof v !== "boolean" &&
    typeof v !== "bigint"
  ) {
    // NOTE: Only primitive scalar values are allowed to be formatted
    return "";
  }

  const s = String(v).trim();
  if (!s) {
    return "";
  }
  // normalize decimal comma to dot for parsing
  const n = Number(s.replace(",", "."));
  if (Number.isNaN(n)) {
    return s;
  }
  return n.toFixed(2);
};
